import type { Envelope } from './types';

// Same-origin in production (the backend serves this bundle) and proxied by
// Vite in development, so one relative base works everywhere.
const BASE = '/api/v1';
const TOKEN_KEY = 'hhg.staff_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// A 401 can surface from any screen, including a background poll. Rather than
// thread a logout callback through every call site, the client announces it and
// the auth provider listens.
export const UNAUTHORIZED_EVENT = 'hhg:unauthorized';

// Every write goes through here, so this is the one place that knows the
// numbers on screen are now stale — an order accepted from the header bar,
// an item taken off a tab, a session paused. Announced the same way as a
// 401: the live views listen (see `useDataVersion`) and refetch at once
// instead of waiting for their next poll, so the dashboard never shows a
// figure that a button on the same screen has just changed.
export const DATA_CHANGED_EVENT = 'hhg:changed';

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Sent as-is with its own type instead of a JSON body, for uploads whose
   *  backend route takes the file itself (the TV lock-screen picture). */
  file?: Blob;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
};

const UNREACHABLE =
  'Cannot reach the server. Check that the HHG server is running and this device is on the cafe Wi-Fi.';

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(BASE + path, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.pathname + url.search;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, file, query, signal } = options;
  const token = getToken();

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      signal,
      headers: {
        ...(file
          ? { 'Content-Type': file.type }
          : body
            ? { 'Content-Type': 'application/json' }
            : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file ?? (body ? JSON.stringify(body) : undefined),
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(UNREACHABLE, 0, {});
  }

  let payload: Envelope<T> | null = null;
  try {
    payload = (await response.json()) as Envelope<T>;
  } catch (error) {
    // Cancelled while the body was still arriving: let the caller ignore it
    // like any other abort, instead of reading `data` off a null payload.
    if ((error as Error).name === 'AbortError') throw error;
    payload = null;
  }

  if (response.status === 401) {
    clearToken();
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
  }

  if (!response.ok || payload?.success === false) {
    // The backend writes `message` for cafe staff to read verbatim, so it is
    // shown as-is rather than mapped to our own copy.
    throw new ApiError(
      payload?.message ?? `Request failed (${response.status}).`,
      response.status,
      payload?.data ?? {},
    );
  }

  if (method !== 'GET') window.dispatchEvent(new Event(DATA_CHANGED_EVENT));

  return (payload as Envelope<T>).data;
}

// A file the backend sends as raw bytes (a stored lock-screen picture) rather
// than an envelope. It needs the staff token, so an <img src> cannot fetch it;
// callers show the Blob through URL.createObjectURL.
export async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const token = getToken();
  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      signal,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(UNREACHABLE, 0, {});
  }

  if (response.status === 401) {
    clearToken();
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
  }
  if (!response.ok) {
    // Errors still come back as the usual envelope, with a staff message.
    const payload = (await response.json().catch(() => null)) as Envelope<unknown> | null;
    throw new ApiError(
      payload?.message ?? `Request failed (${response.status}).`,
      response.status,
      payload?.data ?? {},
    );
  }
  return response.blob();
}
